import { GenericResult } from '@/models/common/results/generic-result';
import { Characteristic } from '@/models/references-classification/characteristic';
import { Reference } from '@/models/references/reference';
import { ReferenceBasicData } from '@/models/references/reference-basic-data';
import { ReferenceImage } from '@/models/references/reference-image';
import { ReferenceSku } from '@/models/references/reference-sku';
import { ReferenceSkuDiscount } from '@/models/references/reference-sku-discount';
import { axiosService } from '.';

export default class ReferencesService {
    public async newReference(reference: ReferenceBasicData): Promise<GenericResult> {
        return new GenericResult((await axiosService.instance.post('references/organization/new', reference)).data);
    }

    public async updateReference(reference: ReferenceBasicData): Promise<boolean> {
        return (await axiosService.instance.post('references/organization/update', reference)).data;
    }

    public async deleteReference(id: number): Promise<boolean> {
        return (await axiosService.instance.post(`references/organization/disable/${id}`)).data;
    }

    public async getReference(id: number): Promise<Reference> {
        return new Reference((await axiosService.instance.get(`references/organization/${id}`)).data);
    }

    public async newReferenceImage(id: number, imageFile: File): Promise<ReferenceImage[]> {
        const formData = new FormData();
        formData.append(imageFile.name, imageFile);

        const response = await axiosService.instance.post(`references/organization/${id}/images/new`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        return response.data.map((image: any) => new ReferenceImage(image));
    }

    public async getReferenceImages(id: number): Promise<ReferenceImage[]> {
        const response = (await axiosService.instance.get(`references/organization/${id}/images`)).data;
        return response.length ? response.map((image: any) => new ReferenceImage(image)) : [];
    }

    public async updateReferenceImage(id: number, imageId: number, imageFile: File): Promise<ReferenceImage[]> {
        const formData = new FormData();
        formData.append(imageFile.name, imageFile);

        const response = await axiosService.instance.post(`references/organization/${id}/images/update/${imageId}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        return response.data.map((image: any) => new ReferenceImage(image));
    }

    public async deleteReferenceImage(id: number, imageId: number): Promise<boolean> {
        return (await axiosService.instance.post(`references/organization/${id}/images/delete/${imageId}`)).data;
    }

    public async updateReferenceTitleDescription(id: number, title?: string, description?: string): Promise<boolean> {
        const Entities = require('html-entities').AllHtmlEntities;
        return (await axiosService.instance.post(`references/organization/${id}/title-description`, { title, description: new Entities().encode(description) })).data;
    }

    public async updateReferenceCategoryCharacteristics(id: number, characteristics: Characteristic[], categoryId?: number): Promise<boolean> {
        return (await axiosService.instance.post(`references/organization/${id}/category-characteristics`, { categoryId, characteristics })).data;
    }

    public async updateReferenceNonPhysicalDetails(id: number, isNonPhysical: boolean, nonPhysicalDeliveryInstructions?: string): Promise<boolean> {
        return (await axiosService.instance.post(`references/organization/${id}/non-physical-details`, { isNonPhysical, nonPhysicalDeliveryInstructions })).data;
    }

    public async getReferenceSkus(id: number): Promise<ReferenceSku[]> {
        const response = await axiosService.instance.get(`references/organization/${id}/skus`);
        return response.data.map((sku: any) => new ReferenceSku(sku));
    }

    public async generateReferenceSkus(id: number, skus: {}): Promise<boolean> {
        return (await axiosService.instance.post(`references/organization/${id}/skus/generate`, skus)).data;
    }

    public async newReferenceSku(id: number, sku: ReferenceSku): Promise<GenericResult> {
        return new GenericResult((await axiosService.instance.post(`references/organization/${id}/skus/new`, sku)).data);
    }

    public async updateReferenceSku(id: number, sku: ReferenceSku): Promise<GenericResult> {
        return new GenericResult((await axiosService.instance.post(`references/organization/${id}/skus/update`, sku)).data);
    }

    public async deleteReferenceSku(id: number, skuId: number): Promise<boolean> {
        return (await axiosService.instance.post(`references/organization/${id}/skus/disable/${skuId}`)).data;
    }

    public async updateReferenceSkus(id: number, skus: ReferenceSku[]): Promise<boolean> {
        return (await axiosService.instance.post(`references/organization/${id}/skus/update-all`, skus)).data;
    }

    public async publishReference(id: number): Promise<boolean> {
        return (await axiosService.instance.post(`references/organization/${id}/publish`)).data;
    }

    public async applyReferenceSkuDiscount(referenceSkuDiscount: ReferenceSkuDiscount): Promise<boolean> {
        return (await axiosService.instance.post(`references/organization/${referenceSkuDiscount.referenceId}/skus/update-discounted-price`, referenceSkuDiscount)).data;
    }

    public async unpublishReference(id: number): Promise<boolean> {
        return (await axiosService.instance.post(`references/organization/${id}/unpublish`)).data;
    }

    public async setReferenceAvailability(id: number, available: boolean): Promise<boolean> {
        return (await axiosService.instance.post(`references/organization/${id}/update-availability?available=${available}`)).data;
    }

    public async setReferenceTimeSensitive(id: number, isTimeSensitive: boolean): Promise<boolean> {
        return (await axiosService.instance.post(`references/organization/${id}/update-time-Sensitivity?isTimeSensitive=${isTimeSensitive}`)).data;
    }

}

export const referencesService = new ReferencesService();
